import { CircleAlert, CircleCheck, Info, Sparkles, TriangleAlert } from 'lucide-react';
import { useTheme } from 'next-themes';
import type { ReactNode } from 'react';
import { Toaster as Sonner, toast as sonnerToast, type ExternalToast } from 'sonner';

import { cn } from '@/lib/utils.js';

type ToasterProps = React.ComponentProps<typeof Sonner>;
type SnackbarVariant = 'success' | 'error' | 'warn' | 'highlight' | 'info';
type SnackbarMessage = ReactNode;

const SNACKBAR_DURATION_MS = 3000;
const SNACKBAR_BASE_CLASSES =
  'inline-flex items-center gap-2 rounded-[var(--border-radius-md)] px-3 py-2 text-[var(--font-size-sm)] leading-[1.3125rem] shadow-[var(--shadow-outer4)] [&_a]:px-1 [&_a]:text-inherit';
const SNACKBAR_VARIANT_CLASSES: Record<SnackbarVariant, string> = {
  success: 'bg-[var(--palette-success-light)] text-[var(--palette-success-dark)]',
  error: 'bg-[var(--palette-error-light)] text-[var(--palette-error-dark)]',
  warn: 'bg-[var(--palette-warning-light)] text-[var(--palette-warning-dark)]',
  highlight: 'bg-[var(--palette-highlight-light)] text-[var(--palette-highlight-dark)]',
  info: 'bg-[var(--palette-info-light)] text-[var(--palette-info-dark)]',
};

const ICON_CLASSES = 'h-4 w-4 shrink-0';

function getVariantIcon(variant: SnackbarVariant): ReactNode {
  switch (variant) {
    case 'success':
      return <CircleCheck className={ICON_CLASSES} />;
    case 'error':
      return <CircleAlert className={ICON_CLASSES} />;
    case 'warn':
      return <TriangleAlert className={ICON_CLASSES} />;
    case 'highlight':
      return <Sparkles className={ICON_CLASSES} />;
    case 'info':
      return <Info className={ICON_CLASSES} />;
    default:
      return null;
  }
}

function withSnackbarDefaults(variant: SnackbarVariant, options?: ExternalToast): ExternalToast {
  return {
    ...options,
    duration: options?.duration ?? SNACKBAR_DURATION_MS,
    icon: options?.icon ?? getVariantIcon(variant),
    className: cn(SNACKBAR_BASE_CLASSES, SNACKBAR_VARIANT_CLASSES[variant], options?.className),
  };
}

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = 'system' } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps['theme']}
      position="top-center"
      offset={35}
      className="toaster group"
      toastOptions={{
        duration: SNACKBAR_DURATION_MS,
        classNames: {
          toast: 'group toast group-[.toaster]:!w-auto group-[.toaster]:!max-w-[560px] group-[.toaster]:!border-0',
          content: 'group-[.toast]:!flex group-[.toast]:!items-center group-[.toast]:!gap-2',
          title: 'group-[.toast]:font-normal',
          description: 'group-[.toast]:text-current group-[.toast]:opacity-100',
          icon: 'group-[.toast]:!m-0',
          actionButton: 'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground',
          cancelButton: 'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground',
        },
      }}
      {...props}
    />
  );
};

const toast = {
  success: (message: SnackbarMessage, options?: ExternalToast) =>
    sonnerToast.success(message, withSnackbarDefaults('success', options)),
  error: (message: SnackbarMessage, options?: ExternalToast) =>
    sonnerToast.error(message, withSnackbarDefaults('error', options)),
  warning: (message: SnackbarMessage, options?: ExternalToast) =>
    sonnerToast.warning(message, withSnackbarDefaults('warn', options)),
  warn: (message: SnackbarMessage, options?: ExternalToast) =>
    sonnerToast.warning(message, withSnackbarDefaults('warn', options)),
  info: (message: SnackbarMessage, options?: ExternalToast) =>
    sonnerToast.info(message, withSnackbarDefaults('info', options)),
  highlight: (message: SnackbarMessage, options?: ExternalToast) =>
    sonnerToast(message, withSnackbarDefaults('highlight', options)),
  dismiss: sonnerToast.dismiss,
  loading: sonnerToast.loading,
  promise: sonnerToast.promise,
  custom: sonnerToast.custom,
};

export { Toaster, toast };
